/**
 * Product Components - Standalone version
 * Contains: variant-select, variant-select-card, variant-select-section,
 * product-variant, product-form, sticky-atc, product-purchase-options
 */

(function() {
  // Early exit if already registered
  if (customElements.get('variant-select')) return;

  // ============================================================================
  // Helper Functions (inlined from helper modules)
  // ============================================================================

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
    };
  }

  // PubSub system
  const productPubSub = (function () {
    let subscribers = {};

    function subscribe(eventName, callback) {
      if (subscribers[eventName] === undefined) {
        subscribers[eventName] = [];
      }
      subscribers[eventName] = [...subscribers[eventName], callback];

      return function unsubscribe() {
        subscribers[eventName] = subscribers[eventName].filter((cb) => {
          return cb !== callback;
        });
      };
    }

    function publish(eventName, data) {
      if (subscribers[eventName]) {
        subscribers[eventName].forEach((callback) => {
          callback(data);
        });
      }
    }

    return { subscribe, publish };
  })();

  const PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    optionValueSelectionChange: 'option-value-selection-change',
    variantChange: 'variant-change',
    cartError: 'cart-error',
    purchaseTypeChange: 'purchase-type-change'
  };

  // Currency utilities
  const CurrencyUtils = {
    getCurrencyCharacter() {
      switch (Shopify.currency.active) {
        case 'EUR':
          return '\u20AC';
        case 'GBP':
          return '\u00A3';
        case 'PLN':
          return 'z\u0142';
        case 'BGN':
          return '\u043B\u0432';
        case 'NOK':
        case 'SEK':
        case 'DKK':
          return 'kr';
        case 'CZK':
          return 'K\u010D';
        case 'HUF':
          return 'Ft';
        case 'CHF':
          return 'CHF';
        case 'CAD':
        case 'AUD':
          return '$';
        case 'USD':
        default:
          return '$';
      }
    },

    formatMoney(cents) {
      const currencySymbol = this.getCurrencyCharacter();
      const isHUF = Shopify.currency.active === 'HUF';

      let amount;
      if (isHUF) {
        amount = Math.round(cents).toString();
      } else {
        amount = (cents / 100).toFixed(2);
      }

      const europeanCurrencies = ['EUR', 'PLN', 'CZK', 'HUF', 'NOK', 'SEK', 'DKK', 'BGN'];
      const useEuropeanFormat = europeanCurrencies.includes(Shopify.currency.active);

      if (useEuropeanFormat) {
        if (!isHUF) {
          const parts = amount.split('.');
          if (parts[0].length > 3) {
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          }
          amount = parts.join(',');
        } else {
          if (amount.length > 3) {
            amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          }
        }

        if (['\u004B\u010D', 'z\u0142', 'Ft', '\u043B\u0432'].includes(currencySymbol)) {
          return amount + ' ' + currencySymbol;
        }
      } else {
        if (amount.length > 3) {
          if (amount.includes('.')) {
            const parts = amount.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            amount = parts.join('.');
          } else {
            amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }
      }

      return currencySymbol + amount;
    },

    parsePrice(priceText) {
      const isHUF = Shopify.currency.active === 'HUF';

      if (isHUF) {
        return parseFloat(priceText.replace(/\./g, ''));
      } else if (priceText.includes(',')) {
        if (priceText.includes('.')) {
          return parseFloat(priceText.replace(/\./g, '').replace(',', '.'));
        } else {
          return parseFloat(priceText.replace(',', '.'));
        }
      } else {
        return parseFloat(priceText.replace(/,/g, ''));
      }
    }
  };

  // ============================================================================
  // DOM Selector Constants
  // ============================================================================

  const SELECTORS = {
    PRODUCT_FORM: 'product-form',
    PRODUCT_ID_INPUT: 'input[name="product-id"]',
    VARIANT_ID_INPUT: 'input[name="id"]',
    SUBSCRIPTION_RADIO: (productId) => `input[id="subscription-${productId}"]`,
    ONE_TIME_RADIO: (productId) => `input[id="one-time-${productId}"]`,
    PURCHASE_TYPE_RADIO: 'input[type="radio"][name="purchase_type"]:checked',
    ADD_TO_CART_BUTTON: 'button[name="add"]',
    PRICE_SPAN: '[data-price-id^="price-"]',
    STICKY_BUTTON: 'sticky-atc button[name="add"]',
    SUBSCRIPTION_OPTIONS: '.subscription-options',
    SUBSCRIPTION_SELECT: 'select[name="selling_plan"]',
    SELLING_PLAN_SELECT: 'select[id^="selling_plan_"]',
    BOTTLE_VARIANT: 'product-variant[data-packaging-type="bottle"].variant-selected-packaging',
    PACKAGING_VARIANT: '.variant-selected-packaging',
    RADIO_CONTAINER: '[data-radio-container]',
    FREEBIE_CONTAINER: (productId) => `#freebie-container-${productId}`,
    ONE_TIME_LABEL: (productId) => `label[for="one-time-${productId}"]`
  };

  // ============================================================================
  // Subscription Manager
  // ============================================================================

  class SubscriptionManager {
    static instances = {};

    constructor(productId, form = null) {
      this.productId = productId;
      this.form = form || document.querySelector('form[data-type="add-to-cart-form"]');
      this.subscriptionRadio = null;
      this.oneTimeRadio = null;
      this.init();
    }

    static getSubscriptionUIController(productId, form = null) {
      if (!SubscriptionManager.instances[productId]) {
        SubscriptionManager.instances[productId] = new SubscriptionManager(productId, form);
      }
      return SubscriptionManager.instances[productId];
    }

    static destroyInstance(productId) {
      if (SubscriptionManager.instances[productId]) {
        SubscriptionManager.instances[productId].cleanupEventListeners();
        delete SubscriptionManager.instances[productId];
      }
    }

    init() {
      this.subscriptionRadio = this.form?.querySelector(SELECTORS.SUBSCRIPTION_RADIO(this.productId));
      this.oneTimeRadio = this.form?.querySelector(SELECTORS.ONE_TIME_RADIO(this.productId));
    }

    updateProductId(newProductId) {
      this.productId = newProductId;
      this.init();
    }

    isSubscriptionSelected() {
      return this.subscriptionRadio?.checked || false;
    }

    getSelectedPurchaseType() {
      return this.isSubscriptionSelected() ? 'subscription' : 'one-time';
    }

    toggleSubscriptionOptions(showSubscription) {
      const benefitsContainer = document.getElementById(`subscription-benefits-${this.productId}`);
      if (benefitsContainer) {
        benefitsContainer.classList.toggle('hidden', !showSubscription);
      }

      const subscriptionOptions = this.form?.querySelector(SELECTORS.SUBSCRIPTION_OPTIONS);
      if (subscriptionOptions) {
        subscriptionOptions.classList.toggle('hidden', !showSubscription);
      }

      this.updateSellingPlanNames(showSubscription);

      const shopPayMessaging = document.getElementById('pdp-shop-pay-messaging');
      if (shopPayMessaging) {
        shopPayMessaging.style.display = showSubscription ? 'none' : 'block';
      }
    }

    updateSellingPlanNames(isSubscription) {
      const idPrefixSelect = this.form?.querySelector(SELECTORS.SELLING_PLAN_SELECT);
      if (idPrefixSelect) {
        idPrefixSelect.name = isSubscription ? 'selling_plan' : '';
      }

      const subscriptionOptions = this.form?.querySelector(SELECTORS.SUBSCRIPTION_OPTIONS);
      const subscriptionSelect = subscriptionOptions?.querySelector('select');
      if (subscriptionSelect) {
        subscriptionSelect.name = isSubscription ? 'selling_plan' : '';
      }
    }

    setupEventListeners(priceUpdateCallback = null) {
      if (this.subscriptionRadio && this.oneTimeRadio) {
        this.cleanupEventListeners();

        this.subscriptionChangeHandler = () => {
          this.toggleSubscriptionOptions(true);
          if (priceUpdateCallback) priceUpdateCallback();
        };

        this.oneTimeChangeHandler = () => {
          this.toggleSubscriptionOptions(false);
          if (priceUpdateCallback) priceUpdateCallback();
        };

        this.subscriptionRadio.addEventListener('change', this.subscriptionChangeHandler);
        this.oneTimeRadio.addEventListener('change', this.oneTimeChangeHandler);

        this.toggleSubscriptionOptions(this.isSubscriptionSelected());
      }
    }

    cleanupEventListeners() {
      if (this.subscriptionRadio && this.subscriptionChangeHandler) {
        this.subscriptionRadio.removeEventListener('change', this.subscriptionChangeHandler);
      }
      if (this.oneTimeRadio && this.oneTimeChangeHandler) {
        this.oneTimeRadio.removeEventListener('change', this.oneTimeChangeHandler);
      }
    }

    updateFreebieContainerVisibility() {
      const freebieContainer = document?.querySelector(SELECTORS.FREEBIE_CONTAINER(this.productId));
      if (!freebieContainer) return;

      const hasFreebies = freebieContainer.children.length > 0;
      if (!hasFreebies) {
        freebieContainer.classList.add('hidden');
        return;
      }

      const isSubscriptionSelected = this.isSubscriptionSelected();
      const selectedPackaging = document.querySelector(SELECTORS.PACKAGING_VARIANT);
      const parentVariant = selectedPackaging?.closest('product-variant');
      let isBottleSelected = parentVariant?.getAttribute('data-packaging-type') === 'bottle';

      if (!isBottleSelected && window.currentProductTags) {
        isBottleSelected = window.currentProductTags.includes('bottle_container');
      }

      if ((isSubscriptionSelected || isBottleSelected) && hasFreebies) {
        freebieContainer.classList.remove('hidden');
      } else {
        freebieContainer.classList.add('hidden');
      }
    }

    selectSubscription() {
      if (this.subscriptionRadio && this.oneTimeRadio) {
        this.subscriptionRadio.checked = true;
        this.oneTimeRadio.checked = false;
        this.toggleSubscriptionOptions(true);
        this.subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    cleanupSubscriptionSelectText() {
      const subscriptionSelect = this.form?.querySelector('.subscription-options');
      if (subscriptionSelect) {
        Array.from(subscriptionSelect.querySelectorAll('option')).forEach((option) => {
          const text = option.text;
          const deliveryText = text.split('-')[0].trim();
          option.text = deliveryText;
        });
      }
    }
  }

  // ============================================================================
  // TryNow Manager
  // ============================================================================

  class TryNowManager {
    static async hideTryNow(isSubscriptionSelected, retryCount = 0) {
      try {
        const tryNowAppBlock = document.querySelector('trynow-cta-button')?.parentElement;

        if (isSubscriptionSelected && !tryNowAppBlock && retryCount < 5) {
          setTimeout(() => TryNowManager.hideTryNow(isSubscriptionSelected, retryCount + 1), 300);
          return;
        }

        if (!tryNowAppBlock) {
          return;
        }

        if (!window.trynow) {
          console.warn('TryNow script not loaded');
          return;
        }

        const tryNowToggle = tryNowAppBlock.querySelector('.tn-gate-toggle-input');
        const tryNowTextToggle = document.getElementById('trynow-toggle');

        let hasPassedGate = false;
        try {
          hasPassedGate = await window.trynow.hasPassedTryLink();
        } catch (trynowError) {
          console.warn('Error checking TryNow gate status:', trynowError);
        }

        if (isSubscriptionSelected) {
          tryNowAppBlock.style.display = 'none';
          if (hasPassedGate && tryNowToggle?.checked) {
            tryNowTextToggle.click();
          }
        } else {
          tryNowAppBlock.style.display = 'block';
          // Re-enable Try Before You Buy for one-time — unless the user explicitly
          // opted out ("No thanks, I'll buy without trying"). That choice persists
          // across product swaps via window._v8TryNowOptedOut (set in trynow-product.liquid).
          if (hasPassedGate && !tryNowToggle?.checked && !window._v8TryNowOptedOut) {
            tryNowTextToggle.click();
          }
        }
      } catch (error) {
        console.error('Error in hideTryNow:', error);
      }
    }
  }

  // ============================================================================
  // DOM Update Manager
  // ============================================================================

  class DOMUpdateManager {
    constructor() {}

    updateProductMedia(doc, currentMedia) {
      const updatedMedia = doc.querySelector('product-media') || doc.querySelector('product-media-new-pdp');
      if (!updatedMedia || !currentMedia) return false;

      if (currentMedia.getAttribute('source') === 'featured-product') {
        const updatedImg = updatedMedia.querySelector('img');
        updatedImg.classList.remove('aspect-square', 'object-container');
        updatedImg.classList.add('aspect-[5/7]', 'object-cover');
        currentMedia.querySelector('img').replaceWith(updatedImg);
      } else {
        const upgradedMedia = currentMedia.cloneNode(false);
        currentMedia.replaceWith(updatedMedia);
        customElements.upgrade(upgradedMedia);
        if (upgradedMedia.generateCarousel) {
          upgradedMedia.generateCarousel();
        }
      }
      return true;
    }

    updateProductTitle(doc) {
      const updatedTitle = doc.querySelector('#productTitle');
      const currentTitle = document.querySelector('#productTitle');
      if (updatedTitle && currentTitle) {
        currentTitle.innerHTML = updatedTitle.innerHTML;
        return true;
      }
      return false;
    }

    updateBreadcrumbs(doc) {
      const updatedBreadCrumb = doc.querySelector('.breadcrumbs');
      const currentBreadcrumb = document.querySelector('.breadcrumbs');
      if (updatedBreadCrumb && currentBreadcrumb) {
        currentBreadcrumb.innerHTML = updatedBreadCrumb.innerHTML;
        return true;
      }
      return false;
    }

    updateContentSections(doc) {
      let updated = false;

      const updatedIngredientsSlider = doc.querySelector('#content-carousel');
      const currentIngredientsSlider = document.querySelector('#content-carousel');
      if (updatedIngredientsSlider && currentIngredientsSlider) {
        currentIngredientsSlider.innerHTML = updatedIngredientsSlider.innerHTML;
        updated = true;
      }

      const updatedFAQs = doc.querySelector('#editorial-faq');
      const currentFAQs = document.querySelector('#editorial-faq');
      if (updatedFAQs && currentFAQs) {
        currentFAQs.innerHTML = updatedFAQs.innerHTML;
        updated = true;
      }

      const updatedEditorialGalleries = doc.querySelector('#product-editorial-galleries');
      const currentEditorialGalleries = document.querySelector('#product-editorial-galleries');
      if (updatedEditorialGalleries && currentEditorialGalleries) {
        currentEditorialGalleries.innerHTML = updatedEditorialGalleries.innerHTML;
        const newGalleries = currentEditorialGalleries.querySelector('product-editorial-galleries');
        if (newGalleries) {
          customElements.upgrade(newGalleries);
        }
        updated = true;
      }

      return updated;
    }

    updateReviewWidget(doc, updatedProductId) {
      let reviewWidget = document.querySelector('.junip-product-review');
      let productSummaryStarWidget = document.querySelector('.junip-product-summary');

      if (!reviewWidget) return false;

      if (reviewWidget) {
        reviewWidget.setAttribute('data-product-id', updatedProductId.value);
      }

      if (productSummaryStarWidget) {
        productSummaryStarWidget.setAttribute('data-product-id', updatedProductId.value);
      }

      if (window.junip && window.junip.init) {
        window.junip.init();
      }

      return true;
    }

    updateTabGroup(doc) {
      const updatedInfo = doc.querySelector('tab-group');
      const currentInfo = document.querySelector('tab-group');
      if (updatedInfo && currentInfo) {
        currentInfo.replaceWith(updatedInfo);
        const upgradedInfo = document.querySelector('tab-group');
        customElements.upgrade(upgradedInfo);
        return true;
      }
      return false;
    }

    updateRecommendedProducts(doc) {
      const updatedProductRecs = doc.querySelector('recommended-products');
      const currentProductRecs = document.querySelector('recommended-products');
      if (updatedProductRecs && currentProductRecs) {
        currentProductRecs.innerHTML = updatedProductRecs.innerHTML;
        return true;
      }
      return false;
    }

    updateFreebieContainer(oldProductId, newProductId) {
      const freebieContainer = document.querySelector(`#freebie-container-${oldProductId}`);
      if (freebieContainer) {
        freebieContainer.id = `freebie-container-${newProductId}`;
        return true;
      }
      return false;
    }

    updateSimpleFaqGroup(doc) {
      const updatedFaqGroup = doc.querySelector('simple-faq-group');
      const currentFaqGroup = document.querySelector('simple-faq-group');
      if (updatedFaqGroup && currentFaqGroup) {
        currentFaqGroup.innerHTML = updatedFaqGroup.innerHTML;
        return true;
      }
      return false;
    }

    updateBeforeAfterSlider(doc) {
      const updatedSlider = doc.querySelector('section-before-after-slider');
      const currentSlider = document.querySelector('section-before-after-slider');
      if (updatedSlider && currentSlider) {
        if (currentSlider.state?.mainCarousel) {
          currentSlider.state.mainCarousel.destroy();
        }
        if (currentSlider.state?.thumbnailCarousel) {
          currentSlider.state.thumbnailCarousel.destroy();
        }

        currentSlider.initialized = false;
        currentSlider.classList.remove('slider-loaded');
        currentSlider.innerHTML = updatedSlider.innerHTML;

        if (currentSlider.connectedCallback) {
          currentSlider.connectedCallback();
        }

        return true;
      }
      return false;
    }
  }

  // ============================================================================
  // Price Manager
  // ============================================================================

  class PriceManager {
    constructor(productId, form = null) {
      this.productId = productId;
      this.form = form || document.querySelector('form[data-type="add-to-cart-form"]');
    }

    extractPriceFromLabel(productId = null) {
      const targetProductId = productId || this.productId;
      const oneTimeLabel = this.form?.querySelector(SELECTORS.ONE_TIME_LABEL(targetProductId));

      if (!oneTimeLabel) {
        console.warn(`One-time label not found for product ID: ${targetProductId}`);
        return null;
      }

      const priceSpan = oneTimeLabel.querySelector('.sr-only');
      let priceText = null;

      if (priceSpan) {
        const fullPriceMatch = priceSpan.textContent.match(/(?:\d+[\.,])*\d+(?:[\.,]\d+)?/);
        if (fullPriceMatch) {
          priceText = fullPriceMatch[0];
        } else {
          const priceMatch = priceSpan.textContent.match(/([^\d]*)(\d+(?:[.,]\d{2})?)/);
          if (priceMatch) priceText = priceMatch[2];
        }
      } else {
        const matches = [...oneTimeLabel.textContent.matchAll(/([^\d]*)(\d+(?:[.,]\d{2})?)/g)];
        const priceMatch = matches.length ? matches[matches.length - 1] : null;
        if (priceMatch) priceText = priceMatch[2];
      }

      if (!priceText) {
        console.warn('No price text found in label');
        return null;
      }

      return CurrencyUtils.parsePrice(priceText);
    }

    calculateFinalPrice(basePrice, isSubscription, discountPercent = null) {
      if (!basePrice || isNaN(basePrice)) return null;

      return basePrice;
    }

    formatPriceForDisplay(price) {
      const isHUF = Shopify.currency.active === 'HUF';
      return isHUF ? CurrencyUtils.formatMoney(price) : CurrencyUtils.formatMoney(price * 100);
    }

    updateButtonPrice(button, formattedPrice, buttonText = 'Add to bag') {
      const priceSpan = button?.querySelector(SELECTORS.PRICE_SPAN);
      if (!priceSpan) {
        console.warn('Price span not found in button');
        return false;
      }

      const priceId = priceSpan.getAttribute('data-price-id');
      button.innerHTML = `
        ${buttonText}&nbsp;\u2014&nbsp;
        <span data-price-id="${priceId}">${formattedPrice}</span>
      `;
      return true;
    }

    updateStickyButton(formattedPrice) {
      const stickyButton = document.querySelector(SELECTORS.STICKY_BUTTON);
      if (stickyButton) {
        const stickyPriceSpan = stickyButton.querySelector(SELECTORS.PRICE_SPAN);
        if (stickyPriceSpan) {
          stickyPriceSpan.textContent = formattedPrice;
          return true;
        }
      }
      return false;
    }

    shouldSkipPriceUpdate(isSubscription, forceUpdate = false) {
      const bottleVariant = document.querySelector(SELECTORS.BOTTLE_VARIANT);
      const isBottleProduct = !!bottleVariant || window.location.pathname.includes('bottle');
      return isBottleProduct && !isSubscription && !forceUpdate;
    }

    updateAllPrices(isSubscription, forceUpdate = false, productId = null) {
      try {
        const targetProductId = productId || this.productId;

        if (this.shouldSkipPriceUpdate(isSubscription, forceUpdate)) {
          return { success: true, skipped: true };
        }

        const basePrice = this.extractPriceFromLabel(targetProductId);
        if (!basePrice) {
          return { success: false, error: 'Could not extract base price' };
        }

        const finalPrice = this.calculateFinalPrice(basePrice, isSubscription);
        if (!finalPrice) {
          return { success: false, error: 'Could not calculate final price' };
        }

        const formattedPrice = this.formatPriceForDisplay(finalPrice);

        const mainButton = this.form?.querySelector(SELECTORS.ADD_TO_CART_BUTTON);
        const buttonUpdated = this.updateButtonPrice(mainButton, formattedPrice);
        const stickyUpdated = this.updateStickyButton(formattedPrice);

        return {
          success: true,
          formattedPrice,
          buttonUpdated,
          stickyUpdated,
          basePrice,
          finalPrice
        };
      } catch (error) {
        console.error('Error in updateAllPrices:', error);
        return { success: false, error: error.message };
      }
    }
  }

  // ============================================================================
  // Custom Elements
  // ============================================================================

  // ProductVariant - Simple element for variant options
  if (!customElements.get('product-variant')) {
    customElements.define(
      'product-variant',
      class ProductVariant extends HTMLElement {
        constructor() {
          super();
        }
      }
    );
  }

  // VariantSelect - Main variant selection component
  customElements.define(
    'variant-select',
    class VariantSelect extends HTMLElement {
      hasInitialized = false;

      constructor() {
        super();

        this.productForm = this.closest('product-form');
        this.productVariants = this.querySelectorAll('product-variant');
        this.selectedVariantOption = this.querySelectorAll('input[name="selected-variant-option"]');
        this.selectedVariantId = this.querySelectorAll('input[name="selected-variant-id"]');
        this.globalProductId = this.productForm?.querySelector(SELECTORS.PRODUCT_ID_INPUT)?.value;

        this.subscriptionManager = null;
        this.priceManager = null;
        this.domUpdateManager = new DOMUpdateManager();
      }

      connectedCallback() {
        if (this.hasInitialized) return;
        this.hasInitialized = true;

        this.initializeVariantSelection();
        this.initializePackagingSelection();
        this.applyEventListeners();
        this.applySubscriptionUI();
      }

      initializeVariantSelection() {
        const selectedSizeVariant = this.querySelector('product-variant.variant-selected-size');
        if (!selectedSizeVariant) return;

        const variantUrl = selectedSizeVariant.getAttribute('variant-value');
        const selectedSizeInput = this.querySelector('#selected-size');

        if (selectedSizeInput && variantUrl) {
          const currentValue = selectedSizeInput.value;
          const hasSize = /(\d+-oz|\d+oz|\d+ml|d+-ml|\d+pack|d+-pack)/.test(currentValue);
          if (!hasSize) {
            selectedSizeInput.value = variantUrl;
          }
        }
      }

      initializePackagingSelection() {
        const hasPackagingVariants = [...this.productVariants].some((variant) =>
          variant.hasAttribute('data-packaging-type')
        );

        if (!hasPackagingVariants) {
          return;
        }

        const productTags = window.currentProductTags || [];
        let currentPackagingType = null;

        if (productTags.includes('bottle_container')) {
          currentPackagingType = 'bottle';
        } else if (productTags.includes('pouch_container')) {
          currentPackagingType = 'pouch';
        }

        [...this.productVariants].forEach((variant) =>
          variant.classList.remove('variant-selected-packaging')
        );

        this.productVariants.forEach((variant) => {
          const packagingType = variant.getAttribute('data-packaging-type');
          if (packagingType === currentPackagingType) {
            variant.classList.add('variant-selected-packaging');
          }
        });
      }

      applyEventListeners() {
        [...this.productVariants].forEach((variant) => {
          variant.addEventListener('click', this.onVariantChange.bind(this));
        });
      }

      initializeManagers(productId = null, form = null) {
        const targetForm = form || this.productForm?.querySelector('form') || this.productForm;
        const productIdInput = targetForm?.querySelector(SELECTORS.PRODUCT_ID_INPUT);
        const targetProductId = productId || productIdInput?.value;

        if (!targetProductId) {
          console.warn('No product ID found for manager initialization');
          return null;
        }

        if (!this.subscriptionManager || this.subscriptionManager.productId !== targetProductId) {
          this.subscriptionManager = SubscriptionManager.getSubscriptionUIController(targetProductId, targetForm);
          this.priceManager = new PriceManager(targetProductId, targetForm);
        }

        return { productId: targetProductId, form: targetForm };
      }

      applySubscriptionUI(productId = null) {
        const result = this.initializeManagers(productId);
        if (!result) return;

        this.subscriptionManager.cleanupSubscriptionSelectText();

        this.subscriptionManager.setupEventListeners(() => {
          this.updateAddToCartPrice(result.productId);
          this.subscriptionManager.updateFreebieContainerVisibility();
        });

        this.updateAddToCartPrice(result.productId);
        this.subscriptionManager.updateFreebieContainerVisibility();
      }

      updateAddToCartPrice = (productId = '', forceUpdate = false) => {
        try {
          const isBottleProduct = document.querySelector(SELECTORS.BOTTLE_VARIANT);
          const hasSubscriptionUI = !!document.querySelector('label[for^="one-time-"]');

          if (isBottleProduct && !hasSubscriptionUI && !forceUpdate) {
            console.log('Skipping price update for bottle product without subscription UI');
            return;
          }

          const result = this.initializeManagers(productId);
          if (!result) {
            console.warn('Failed to initialize managers in updateAddToCartPrice');
            return;
          }

          const isSubscriptionSelected = this.subscriptionManager?.isSubscriptionSelected() || false;

          const priceResult = this.priceManager.updateAllPrices(
            isSubscriptionSelected,
            forceUpdate,
            result.productId
          );

          if (!priceResult.success && !priceResult.skipped) {
            console.warn('Price update failed:', priceResult.error);
            return;
          }

          TryNowManager.hideTryNow(isSubscriptionSelected);
        } catch (error) {
          console.error('Error in updateAddToCartPrice:', error);
        }
      };

      async renderProductInformation(targetValue) {
        try {
          const response = await fetch(targetValue);

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          if (window.location.pathname.includes('/products')) {
            history.pushState({}, '', targetValue);
          }

          const data = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');

          const scriptContent = data.match(/window\.currentProductTags\s*=\s*(\[.*?\]);/);
          if (scriptContent && scriptContent[1]) {
            try {
              window.currentProductTags = JSON.parse(scriptContent[1]);
            } catch (e) {
              console.warn('Failed to parse product tags:', e);
            }
          }

          const updatedForm = doc.querySelector('#product-atc');
          const updatedProductId = doc.querySelector('input[name="product-id"]');
          const updatedVariantSelectorsNewPdp = doc.querySelector('#product-variant-selectors-new-pdp');
          const updatedSubscriptionOptionsNewPdp = doc.querySelector('[data-subscription-select]');

          if (updatedProductId) {
            const currentProductId = this.productForm.querySelector('input[name="product-id"]');
            if (currentProductId && currentProductId.value !== updatedProductId.value) {
              const oldProductId = currentProductId.value;
              const newProductId = updatedProductId.value;

              this.domUpdateManager.updateFreebieContainer(oldProductId, newProductId);
              this.domUpdateManager.updateReviewWidget(doc, updatedProductId);
            }
          }

          this.domUpdateManager.updateContentSections(doc);
          this.domUpdateManager.updateBreadcrumbs(doc);
          this.domUpdateManager.updateSimpleFaqGroup(doc);
          this.domUpdateManager.updateBeforeAfterSlider(doc);

          const currentMedia =
            document.querySelector('product-media') || document.querySelector('product-media-new-pdp');
          if (!this.domUpdateManager.updateProductMedia(doc, currentMedia)) {
            console.error('Updated media section not found in the response');
          }

          this.domUpdateManager.updateProductTitle(doc);

          // Capture purchase-type state BEFORE any DOM replacement
          const priorRadio = document.querySelector('input[type="radio"][name="purchase_type"]:checked');
          const wasSub = priorRadio?.value === 'subscription';
          const priorSellingPlan = document.querySelector('select[data-subscription-select]')?.value || null;

          if (updatedForm) {
            const currentForm = document.querySelector('#product-atc');
            const currentVariantSelectorsNewPdp = document.querySelector('#product-variant-selectors-new-pdp');
            const currentSubscriptionOptionsNewPdp = document.querySelector('[data-subscription-select]');

            const updatedFullForm = doc.querySelector('product-form form');
            const currentFullForm = document.querySelector('product-form form');

            const purchaseTypeLabel = currentFullForm?.querySelector('#purchase-type-label');

            const updatedPurchaseOptions = updatedFullForm?.querySelector('.purchase-options');
            const currentPurchaseOptions = currentFullForm?.querySelector('.purchase-options');

            if (!updatedPurchaseOptions && currentPurchaseOptions) {
              currentPurchaseOptions.classList.add('hidden');
              if (purchaseTypeLabel) {
                purchaseTypeLabel.classList.add('hidden');
              }

              const oneTimeRadio = currentFullForm.querySelector(`input[id^="one-time-"]`);

              if (oneTimeRadio && !oneTimeRadio.checked) {
                oneTimeRadio.checked = true;
                oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else if (updatedPurchaseOptions && !currentPurchaseOptions) {
              let werePurchaseOptionsInserted = false;

              werePurchaseOptionsInserted = this.insertPurchaseOptions(updatedFullForm, currentFullForm);

              if (werePurchaseOptionsInserted) {
                const variantSelect = document.querySelector('variant-select');
                if (variantSelect) {
                  variantSelect.applySubscriptionUI();
                }
              }
            } else if (updatedPurchaseOptions && currentPurchaseOptions) {
              currentPurchaseOptions.classList.remove('hidden');
              if (purchaseTypeLabel) {
                purchaseTypeLabel.classList.remove('hidden');
              }
            }

            // Pre-patch the incoming HTML to reflect the prior purchase-type state
            // so there is no flicker when the DOM is swapped
            if (wasSub) {
              const incomingOneTime = updatedForm.querySelector('input[value="one-time"][data-purchase-type-radio]');
              const incomingSub = updatedForm.querySelector('input[value="subscription"][data-purchase-type-radio]');
              if (incomingOneTime) incomingOneTime.removeAttribute('checked');
              if (incomingSub) incomingSub.setAttribute('checked', '');

              // Show the subscription options panel
              const incomingSubOptions = updatedForm.querySelector('.subscription-options');
              if (incomingSubOptions) incomingSubOptions.classList.remove('hidden');

              // Set the selling_plan select name so it submits with the form
              const incomingSelect = updatedForm.querySelector('select[data-subscription-select]');
              if (incomingSelect) {
                incomingSelect.name = 'selling_plan';
                // Restore the previously selected frequency if available
                if (priorSellingPlan) {
                  const matchingOpt = incomingSelect.querySelector(`option[value="${priorSellingPlan}"]`);
                  if (matchingOpt) {
                    // Clear any existing selection and select the prior one
                    Array.from(incomingSelect.options).forEach(o => o.removeAttribute('selected'));
                    matchingOpt.setAttribute('selected', 'selected');
                  }
                }
              }

              // Update background highlights on the radio containers
              const incomingOneTimeContainer = incomingOneTime?.closest('[data-radio-container]');
              const incomingSubContainer = incomingSub?.closest('[data-radio-container]');
              if (incomingOneTimeContainer) {
                incomingOneTimeContainer.classList.remove('bg-brand-secondary-400');
                incomingOneTimeContainer.classList.add('bg-white');
              }
              if (incomingSubContainer) {
                incomingSubContainer.classList.remove('bg-white');
                incomingSubContainer.classList.add('bg-brand-secondary-400');
              }
            }

            if (!currentForm.querySelector('sticky-atc')) {
              updatedForm.querySelector('sticky-atc')?.remove();
            }

            if (currentForm.dataset.externalVariantSelectors === 'true') {
              updatedForm.querySelectorAll('variant-select').forEach((el) => el.remove());
            }

            currentForm.innerHTML = updatedForm.innerHTML;

            if (updatedVariantSelectorsNewPdp && currentVariantSelectorsNewPdp) {
              currentVariantSelectorsNewPdp.innerHTML = updatedVariantSelectorsNewPdp.innerHTML;
            }

            if (updatedSubscriptionOptionsNewPdp) {
              if (currentSubscriptionOptionsNewPdp) {
                currentSubscriptionOptionsNewPdp.innerHTML = updatedSubscriptionOptionsNewPdp.innerHTML;
              }
            }

            const checkForSubscriptionUI = setInterval(() => {
              const bulletsContainer = document.querySelector('.subscription-bullets-container');

              if (bulletsContainer && window.updateSubscriptionBullets) {
                clearInterval(checkForSubscriptionUI);
                window.updateSubscriptionBullets(updatedProductId.value);
              }
            }, 100);

            setTimeout(() => clearInterval(checkForSubscriptionUI), 5000);

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = data;
            const newProductId = updatedProductId.value;

            const currentOneTimeLabel = document.querySelector('label[for^="one-time-"]');
            const newOneTimeLabel = tempDiv.querySelector(`label[for="one-time-${newProductId}"]`);

            if (currentOneTimeLabel && newOneTimeLabel) {
              const srOnlySpan = newOneTimeLabel.querySelector('.sr-only');
              if (srOnlySpan) {
                const fullPriceText = srOnlySpan.textContent;
                currentOneTimeLabel.innerHTML = `One-time purchase <span class="sr-only">${fullPriceText}</span>`;
              }
            }

            const newSubscriptionSelect = tempDiv.querySelector(`#selling_plan_${newProductId}`);

            if (newSubscriptionSelect) {
              const currentSelect = document.querySelector('select[id^="selling_plan_"]');

              if (currentSelect) {
                const updatedSelect = document.createElement('select');
                updatedSelect.id = `selling_plan_${newProductId}`;
                updatedSelect.className = currentSelect.className;
                updatedSelect.name = wasSub ? 'selling_plan' : '';

                const options = Array.from(newSubscriptionSelect.options);

                const modifiedOptions = options.map((option) => {
                  const text = option.text;
                  const deliveryText = text.split('-')[0].trim();

                  const newOption = document.createElement('option');
                  newOption.value = option.value;
                  newOption.text = deliveryText;
                  return newOption;
                });

                modifiedOptions.forEach((option) => updatedSelect.appendChild(option));
                currentSelect.parentNode.replaceChild(updatedSelect, currentSelect);
              }
            }

            const updatedAddToCartForm = doc.querySelector('form[data-type="add-to-cart-form"]');

            currentForm.innerHTML = updatedForm.innerHTML;

            const newCurrentAddToCartForm = document.querySelector('form[data-type="add-to-cart-form"]');
            if (
              updatedAddToCartForm?.getAttribute('id') &&
              updatedAddToCartForm.getAttribute('id') !== newCurrentAddToCartForm?.getAttribute('id')
            ) {
              newCurrentAddToCartForm?.setAttribute('id', updatedAddToCartForm.getAttribute('id'));
            }

            const currentProductIdElement = this.productForm.querySelector('input[name="product-id"]');
            const oldProductId = currentProductIdElement?.value || this.globalProductId;
            const updatedProductIdValue = updatedProductId.value;

            if (oldProductId && updatedProductIdValue && oldProductId !== updatedProductIdValue) {
              SubscriptionManager.destroyInstance(oldProductId);

              const oldRadios = document.querySelectorAll(`[id*="${oldProductId}"]`);
              oldRadios.forEach((element) => {
                const oldId = element.id;
                const newId = oldId.replace(oldProductId, updatedProductIdValue);
                element.id = newId;
              });

              const oldLabels = document.querySelectorAll(`[for*="${oldProductId}"]`);
              oldLabels.forEach((element) => {
                const oldFor = element.getAttribute('for');
                const newFor = oldFor.replace(oldProductId, updatedProductIdValue);
                element.setAttribute('for', newFor);
              });
            }

            // Label text is rendered by Liquid — no JS override needed

            const subscriptionRadio = document.getElementById(`subscription-${updatedProductIdValue}`);
            const oneTimeRadio = document.getElementById(`one-time-${updatedProductIdValue}`);

            if (subscriptionRadio && oneTimeRadio) {
              this.updateAddToCartPrice(updatedProductId.value);
            }
          }

          this.domUpdateManager.updateTabGroup(doc);
          this.domUpdateManager.updateRecommendedProducts(doc);

          if (updatedProductId) {
            this.domUpdateManager.updateReviewWidget(doc, updatedProductId);
          }

          if (updatedProductId) {
            const currentProductId = this.productForm.querySelector('input[name="product-id"]');

            if (currentProductId) {
              const oldProductId = currentProductId.value;
              currentProductId.value = updatedProductId.value;

              window.dispatchEvent(
                new CustomEvent('product-id-changed', {
                  detail: {
                    oldProductId: oldProductId,
                    newProductId: updatedProductId.value
                  }
                })
              );
            }
          }

          window.dispatchEvent(new CustomEvent('recalculate-try-now-state'));

          setTimeout(() => {
            // Use wasSub captured BEFORE innerHTML replacement (not re-read from DOM)
            const wasSubscriptionSelected = wasSub;

            const updatedProductId = document.querySelector('input[name="product-id"]')?.value;

            const newRadios = document.querySelectorAll('input[data-purchase-type-radio]');
            if (newRadios.length > 0) {
              newRadios.forEach((radio) => {
                if (
                  (radio.value === 'subscription' && wasSubscriptionSelected) ||
                  (radio.value === 'one-time' && !wasSubscriptionSelected)
                ) {
                  radio.checked = true;
                  radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
              });

              // Restore the previously selected selling plan frequency
              if (wasSubscriptionSelected && priorSellingPlan) {
                const newSelect = document.querySelector('select[data-subscription-select]');
                if (newSelect) {
                  const matchingOption = Array.from(newSelect.options).find(o => o.value === priorSellingPlan);
                  if (matchingOption) {
                    newSelect.value = priorSellingPlan;
                  }
                }
              }

              if (window.pendingSubscriptionSelection) {
                const currentUrl = new URL(window.location.href);
                this.onPouchClick(currentUrl);
                window.pendingSubscriptionSelection = false;
              } else if (!wasSubscriptionSelected) {
                const form = newRadios[0]?.closest('form');
                if (form) {
                  const idPrefixSelect = form.querySelector('select[id^="selling_plan_"]');
                  if (idPrefixSelect) {
                    idPrefixSelect.name = '';
                  }

                  const subscriptionOptions = form.querySelector('.subscription-options');
                  const subscriptionSelect = subscriptionOptions?.querySelector('select');
                  if (subscriptionSelect) {
                    subscriptionSelect.name = '';
                  }
                }
              }
            }

            const variantSelect = document.querySelector('variant-select');
            if (variantSelect && updatedProductId) {
              if (window.switchingToBottle) {
                window.switchingToBottle = false;
                return;
              }

              this.applySubscriptionUI(updatedProductId.value);

              // Ensure subscription panel visibility matches the restored state
              // applySubscriptionUI may have toggled it based on stale radio refs
              if (wasSubscriptionSelected && this.subscriptionManager) {
                this.subscriptionManager.toggleSubscriptionOptions(true);
              }

              this.updateAddToCartPrice(updatedProductId.value, false);
            }
          }, 100);
        } catch (error) {
          console.error('Error fetching & updated section:', error);
        } finally {
          setTimeout(() => {
            window.isVariantSwitching = false;
          }, 200);
        }
      }

      insertPurchaseOptions(updatedFullForm, currentFullForm) {
        const updatedPurchaseOptions = updatedFullForm?.querySelector('.purchase-options');
        const updatedPurchaseTypeLabel = updatedFullForm?.querySelector('#purchase-type-label');
        const productAtcDiv = currentFullForm.querySelector('#product-atc');

        if (!productAtcDiv || !updatedPurchaseOptions || !updatedPurchaseTypeLabel) {
          return false;
        }

        const labelClone = updatedPurchaseTypeLabel.cloneNode(true);
        productAtcDiv.parentNode.insertBefore(labelClone, productAtcDiv);

        const purchaseOptionsClone = updatedPurchaseOptions.cloneNode(true);

        const subscriptionOptions = purchaseOptionsClone.querySelector('.subscription-options');
        const subscriptionBenefits = subscriptionOptions?.querySelector('.subscription-benefits');
        const subscriptionContainer = purchaseOptionsClone
          .querySelector('input[value="subscription"]')
          ?.closest('[data-radio-container]');

        if (subscriptionContainer) {
          subscriptionContainer.classList.remove('bg-brand-secondary-400');
          subscriptionContainer.classList.add('bg-white');
        }

        if (subscriptionOptions) {
          subscriptionOptions.classList.remove('hidden');
        }

        if (subscriptionBenefits) {
          subscriptionBenefits.classList.remove('hidden');
        }

        productAtcDiv.parentNode.insertBefore(purchaseOptionsClone, productAtcDiv);

        return true;
      }

      onPouchClick(currentUrl) {
        const currentProductId = document.querySelector('input[name="product-id"]')?.value;

        if (currentProductId && this.subscriptionManager?.productId !== currentProductId) {
          this.subscriptionManager.updateProductId(currentProductId);
        }

        if (this.subscriptionManager) {
          this.subscriptionManager.init();
          this.subscriptionManager.selectSubscription();
        }

        currentUrl.searchParams.delete('purchase_type');
        history.pushState({}, '', currentUrl.toString());
      }

      onVariantChange(event) {
        event.preventDefault();

        window.isVariantSwitching = true;

        let isVariantType = false;
        [...this.productVariants].forEach((variant) => {
          if (variant.classList.contains('variant-selected-type')) {
            isVariantType = true;
          }
        });

        if (isVariantType) {
          [...this.productVariants].forEach((variant) => variant.classList.remove('variant-selected-type'));
          event.target.classList.add('variant-selected-type');
        } else {
          [...this.productVariants].forEach((variant) => variant.classList.remove('variant-selected-size'));
          event.target.classList.add('variant-selected-size');
        }

        const packagingType = event.target.getAttribute('data-packaging-type');

        if (packagingType) {
          [...this.productVariants].forEach((variant) =>
            variant.classList.remove('variant-selected-packaging')
          );
          event.target.classList.add('variant-selected-packaging');
        }

        const isBottleVariant = event.target.getAttribute('data-packaging-type') === 'bottle';

        let targetValue = event.target.getAttribute('variant-value');
        let targetId = event.target.getAttribute('variant-id');

        this.selectedVariantOption.forEach((option) => {
          option.value = targetValue;
        });
        this.selectedVariantId.forEach((option) => {
          option.value = targetId;
        });

        let selectedSizeElement = this.productForm.querySelector('#selected-size');
        let targetSize = selectedSizeElement ? selectedSizeElement.value : null;
        let match = targetSize ? targetSize.match(/(\d+-oz|\d+oz|\d+ml|d+-ml|\d+pack|d+-pack)/) : null;
        let sizeToReplace = match ? match[1] : null;

        if (sizeToReplace) {
          targetValue = targetValue.replace(/(\d+-oz|\d+oz|\d+ml|d+-ml|\d+pack|d+-pack)/, sizeToReplace);
        } else {
          console.log('No size found in the first URL to replace.');
        }

        if (isBottleVariant) {
          window.switchingToBottle = true;
        }

        if (targetValue.includes('purchase_type=')) {
          window.pendingSubscriptionSelection = true;
        }

        this.renderProductInformation(targetValue);
      }
    }
  );

  // VariantSelectCard - Card-specific variant selection
  if (!customElements.get('variant-select-card')) {
    customElements.define(
      'variant-select-card',
      class VariantSelectCard extends HTMLElement {
        constructor() {
          super();

          this.productForm = this.closest('product-form');
          this.productVariants = this.querySelectorAll('product-variant');
          this.selectedVariantOption = this.querySelector('input[name="selected-variant-option"]');
          this.selectedVariantId = this.querySelector('input[name="selected-variant-id"]');
          this.variantIdInput = this.productForm?.querySelector('input[name="id"]');

          const productJsonScript = this.querySelector('script[data-product-json]');
          this.productJson = productJsonScript ? JSON.parse(productJsonScript.textContent) : null;
          this.selectedVariant = this.productJson?.variants?.find(
            (v) => v.id.toString() === this.variantIdInput?.value
          );
          this.productId = this.productJson?.id;
        }

        connectedCallback() {
          this.applyEventListeners();
        }

        applyEventListeners() {
          this.productVariants.forEach((variant) => {
            variant.addEventListener('click', this.onVariantChange.bind(this));
          });

          productPubSub.subscribe(PUB_SUB_EVENTS.purchaseTypeChange, (event) => {
            if (event.productId.toString() !== this.productId?.toString()) {
              return;
            }

            this.updatePrices({
              oldProductId: event.productId,
              newProductId: event.productId,
              selectedVariant: this.selectedVariant,
              isSubscriptionSelected: event.purchaseType === 'subscription'
            });
          });
        }

        async renderProductInformation() {
          if (this.variantIdInput && this.selectedVariantId) {
            this.variantIdInput.value = this.selectedVariantId.value;
          }
        }

        onVariantChange(event) {
          event.preventDefault();

          this.productVariants.forEach((variant) => variant.classList.remove('variant-selected-size'));
          event.target.classList.add('variant-selected-size');

          let targetProductHandle = event.target.getAttribute('variant-value');
          let targetVariantId = event.target.getAttribute('variant-id');

          if (this.selectedVariantOption) {
            this.selectedVariantOption.value = targetProductHandle;
          }
          if (this.selectedVariantId) {
            this.selectedVariantId.value = targetVariantId;
          }
          if (this.variantIdInput) {
            this.variantIdInput.value = targetVariantId;
          }

          this.fetchVariantData(targetProductHandle, targetVariantId);
        }

        updatePrices({ oldProductId, newProductId, selectedVariant, isSubscriptionSelected = false }) {
          console.log('updatePrices', {
            oldProductId,
            newProductId,
            selectedVariant,
            isSubscriptionSelected
          });

          let cardProduct = this.closest('card-product');
          if (!cardProduct && this.productForm) {
            cardProduct = this.productForm.closest('card-product');
          }

          if (cardProduct && selectedVariant) {
            let trynowPriceContainer = cardProduct.querySelector(`#card-trynow-price-${oldProductId}`);
            if (!trynowPriceContainer && oldProductId !== newProductId) {
              trynowPriceContainer = cardProduct.querySelector(`#card-trynow-price-${newProductId}`);
            }

            if (trynowPriceContainer) {
              if (oldProductId !== newProductId) {
                trynowPriceContainer.id = `card-trynow-price-${newProductId}`;
              }

              const trynowPriceSpan = trynowPriceContainer.querySelector('[data-trynow-price]');

              if (trynowPriceSpan) {
                trynowPriceSpan.setAttribute('data-trynow-price', selectedVariant.price);
                trynowPriceSpan.textContent = CurrencyUtils.formatMoney(selectedVariant.price);
              }

              const trynowComparePriceSpan = trynowPriceContainer.querySelector('[data-trynow-compare-price]');
              if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
                if (!trynowComparePriceSpan) {
                  const newCompareSpan = document.createElement('span');
                  newCompareSpan.className = 'trynow-original-price';
                  newCompareSpan.setAttribute('data-trynow-compare-price', selectedVariant.compare_at_price);
                  newCompareSpan.textContent = CurrencyUtils.formatMoney(selectedVariant.compare_at_price);
                  const flexContainer = trynowPriceContainer.querySelector('.flex');
                  if (flexContainer && trynowPriceSpan) {
                    flexContainer.insertBefore(newCompareSpan, trynowPriceSpan);
                  }
                } else {
                  trynowComparePriceSpan.setAttribute('data-trynow-compare-price', selectedVariant.compare_at_price);
                  trynowComparePriceSpan.textContent = CurrencyUtils.formatMoney(selectedVariant.compare_at_price);
                  trynowComparePriceSpan.style.display = '';
                }
              } else if (trynowComparePriceSpan) {
                trynowComparePriceSpan.style.display = 'none';
              }
            } else {
              console.warn('TryNow price container not found for product:', oldProductId);
            }
          } else {
            console.warn('TryNow update - cardProduct or selectedVariant missing:', {
              cardProduct: !!cardProduct,
              selectedVariant: !!selectedVariant
            });
          }

          this.priceElements = Array.from(document.querySelectorAll(`[data-price-id="price-${oldProductId}"]`));

          if (!this.priceElements.length) {
            return;
          }

          this.priceElements = this.priceElements.map((priceElement) => {
            priceElement.setAttribute('data-price-id', `price-${newProductId}`);

            let priceText = document.createElement('span');
            let compareText = document.createElement('span');
            compareText.classList.add('line-through', 'text-brand-secondary-350');

            priceText.textContent = CurrencyUtils.formatMoney(selectedVariant.price);

            if (selectedVariant.compare_at_price) {
              const compareAtPrice = CurrencyUtils.formatMoney(selectedVariant.compare_at_price);
              compareText.textContent = `${compareAtPrice}`;
              priceText.prepend('\u00A0');
              priceText.prepend(compareText);
            }

            priceElement.textContent = '';
            priceElement.append(priceText);
            return priceElement;
          });
        }

        async fetchVariantData(productHandle, variantId) {
          console.log('fetchVariantData', {
            productHandle,
            variantId
          });

          try {
            const previousProductId = this.productForm?.querySelector('input[name="product-id"]')?.value;
            const response = await fetch(`${productHandle.split('?')[0]}.js`);

            if (!response.ok) throw new Error('Network response was not ok');

            const productData = await response.json();

            this.selectedVariant = productData.variants.find(
              (variant) => variant.id.toString() === variantId
            );

            const cardProduct = this.closest('card-product');
            const newProductId = String(productData.id);
            this.productId = newProductId;

            if (this.selectedVariant) {
              this.productIdInputs = Array.from(document.querySelectorAll('input[name="product-id"]'));

              this.updatePrices({
                oldProductId: previousProductId,
                newProductId: newProductId,
                selectedVariant: this.selectedVariant
              });

              if (cardProduct && productData.featured_image) {
                const productImages = cardProduct.querySelectorAll('img');
                productImages.forEach((img) => {
                  if (img.closest('.badge-image')) {
                    return;
                  }

                  const currentSrc = img.src;
                  const currentSrcset = img.srcset;

                  let sizeMatch = currentSrc.match(/width=(\d+)/);
                  let width = sizeMatch
                    ? sizeMatch[1]
                    : img.width || img.getAttribute('width') || '400';

                  const newImageUrl = productData.featured_image + '&width=' + width;

                  img.src = newImageUrl;

                  if (currentSrcset) {
                    const srcsetParts = currentSrcset.split(',').map((part) => {
                      const [url, descriptor] = part.trim().split(' ');
                      const widthMatch = url.match(/width=(\d+)/);
                      if (widthMatch) {
                        const srcsetWidth = widthMatch[1];
                        return `${productData.featured_image}&width=${srcsetWidth} ${descriptor}`;
                      }
                      return part;
                    });
                    img.srcset = srcsetParts.join(', ');
                  }

                  if (productData.title) {
                    img.alt = productData.title;
                  }
                });
              }

              if (newProductId !== previousProductId) {
                let cardAtcContainer = document.querySelector(`#card-atc-${previousProductId}`);

                if (cardAtcContainer) {
                  cardAtcContainer.id = `card-atc-${newProductId}`;
                }
              }

              if (previousProductId && previousProductId !== newProductId) {
                if (this.productIdInputs.length > 0) {
                  this.productIdInputs = this.productIdInputs.map((input) => {
                    const clonedInput = input.cloneNode(true);
                    clonedInput.value = newProductId;
                    return clonedInput;
                  });

                  this.productIdInputs.forEach((newInput) => {
                    const previousInput = document.querySelector('input[name="product-id"]');
                    if (previousInput) {
                      previousInput.parentNode.replaceChild(newInput, previousInput);
                    }
                  });
                }
              }

              if (this.variantIdInput) {
                this.variantIdInput.value = variantId;
              }
              if (this.selectedVariantId) {
                this.selectedVariantId.value = variantId;
              }

              const productFormIdInput = this.productForm?.querySelector('input[name="product-id"]');

              if (productFormIdInput) {
                productFormIdInput.value = newProductId;
              }
            }

            if (cardProduct) {
              const productPurchaseOptions = cardProduct.querySelector('product-purchase-options');

              if (productPurchaseOptions) {
                productPurchaseOptions.init(newProductId, productData.selling_plan_groups);
              }
            }
          } catch (error) {
            console.error('Error fetching variant data:', error);
          }
        }
      }
    );
  }

  // VariantSelectSection - Section-specific variant selection
  if (!customElements.get('variant-select-section')) {
    customElements.define(
      'variant-select-section',
      class VariantSelectSection extends HTMLElement {
        constructor() {
          super();
        }
      }
    );
  }

  // ProductForm — custom element wrapper for the ATC form.
  // Submission is handled by v2-cart-drawer's capture-phase document submit
  // listener (cart-drawer.js). This component only needs to exist so that
  // other code can reference the <product-form> element for DOM traversal.
  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        constructor() {
          super();
          this.form = this.querySelector('form');
        }
      }
    );
  }

  // StickyATC - Sticky add to cart button
  if (!customElements.get('sticky-atc')) {
    customElements.define(
      'sticky-atc',
      class StickyATC extends HTMLElement {
        constructor() {
          super();
          this.addToBagButton = this.closest('#product-atc')?.querySelector('button[name="add"]');
          this.headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
          this.hasUserScrolled = false;
        }

        connectedCallback() {
          this.style.display = 'none';

          const scrollHandler = () => {
            this.hasUserScrolled = true;
            window.removeEventListener('scroll', scrollHandler);
          };
          window.addEventListener('scroll', scrollHandler);

          setTimeout(() => {
            const observeTarget =
              document.querySelector('#product-variant-selectors-new-pdp') ||
              document.querySelector('#type-selector-pdp-new') ||
              document.querySelector('#productTitle') ||
              this.addToBagButton;

            if (!observeTarget) return;

            const observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting && this.hasUserScrolled) {
                    this.style.display = 'flex';
                    this.classList.add('lg:top-[var(--header-height)]');
                    this.classList.add('lg:bottom-auto');
                    this.classList.remove('lg:bottom-0');
                  } else if (entry.isIntersecting && this.hasUserScrolled) {
                    this.style.display = 'none';
                    this.classList.remove('lg:top-[var(--header-height)]');
                    this.classList.add('lg:bottom-0');
                    this.classList.remove('lg:bottom-auto');
                  }
                });
              },
              {
                root: null,
                rootMargin: `-${this.headerHeight} 0px 0px 0px`,
                threshold: 0
              }
            );

            observer.observe(observeTarget);
          }, 100);
        }
      }
    );
  }

  // ProductPurchaseOptions - Subscription/one-time toggle
  if (!customElements.get('product-purchase-options')) {
    customElements.define(
      'product-purchase-options',
      class ProductPurchaseOptions extends HTMLElement {
        constructor() {
          super();
        }

        connectedCallback() {
          this.productId = this.getAttribute('data-product-id');
          this.init(this.productId);
        }

        init(productId = this.productId, sellingPlanGroups = []) {
          const defaultSubscriptionBullets = [
            'Free Shipping on every subscription order',
            'Early Access to new product launches',
            'Bonus offers throughout the year'
          ];

          const oneTimeRadio = this.querySelector('[name="purchase_type"][value="one-time"]');
          const subscriptionRadio = this.querySelector('[name="purchase_type"][value="subscription"]');
          const benefitsContainer = this.querySelector('.subscription-benefits');
          const bulletsContainer = benefitsContainer?.querySelector('.subscription-bullets-container');

          // Label text is rendered by Liquid — no JS override needed

          let subscriptionBullets = [...defaultSubscriptionBullets];
          if (window.subscriptionBulletsDict && window.subscriptionBulletsDict[productId]) {
            subscriptionBullets = window.subscriptionBulletsDict[productId];
          }

          if (bulletsContainer) {
            let allBullets = subscriptionBullets.map(
              (bullet) => `
              <p class="flex items-center w-full mt-2 text-brand-secondary-200">
                  <span class="mr-2">
                  <svg class="w-4 h-4 p-[1px] border rounded-full border-brand-secondary-200" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  </span>
                  <span class="text-sm font-base leading-[14px]">${bullet}</span>
              </p>
          `
            );

            if (window.subscriptionExceptionDict?.[productId]) {
              window.subscriptionExceptionDict?.[productId].forEach((bullet) => {
                allBullets.push(`
                  <p class="flex items-center w-full mt-2 text-brand-secondary-200">
                      <span class="text-xs font-base leading-[14px] italic">${bullet}</span>
                  </p>
              `);
              });
            }

            bulletsContainer.innerHTML = allBullets.join('');
          }

          const toggleSubscriptionOptions = (showSubscription) => {
            productPubSub.publish(PUB_SUB_EVENTS.purchaseTypeChange, {
              source: 'product-purchase-options',
              productId: productId,
              purchaseType: showSubscription ? 'subscription' : 'one-time'
            });

            if (benefitsContainer) {
              benefitsContainer.classList.toggle('hidden', !showSubscription);
            }

            const shopPayMessaging = document.getElementById('pdp-shop-pay-messaging');
            if (shopPayMessaging) {
              shopPayMessaging.style.display = showSubscription ? 'none' : 'block';
            }

            const subscriptionSelect = this.querySelector('.subscription-options');
            if (subscriptionSelect) {
              subscriptionSelect.classList.toggle('hidden', !showSubscription);
              const select = subscriptionSelect.querySelector('select');
              if (select) {
                select.name = showSubscription ? 'selling_plan' : '';
              }
            }
          };

          const availableSellingPlanGroups = sellingPlanGroups?.filter((group) => group.app_id === 'SKIO');
          if (availableSellingPlanGroups?.length) {
            const subscriptionSelect = this.querySelector('.subscription-options select');
            if (subscriptionSelect) {
              subscriptionSelect.innerHTML = '';
              availableSellingPlanGroups
                .flatMap((group) => group.selling_plans)
                .forEach((plan, index) => {
                  const option = document.createElement('option');
                  option.value = plan.id;
                  option.textContent = plan.name;
                  if (index === 0) {
                    option.selected = true;
                  }
                  subscriptionSelect.appendChild(option);
                });
            }
          }

          if (subscriptionRadio) {
            toggleSubscriptionOptions(subscriptionRadio.checked);
            subscriptionRadio.addEventListener('change', (event) => {
              toggleSubscriptionOptions(event.target.checked);
            });
          }

          if (oneTimeRadio) {
            oneTimeRadio.addEventListener('change', (event) => {
              toggleSubscriptionOptions(!event.target.checked);
            });
          }
        }
      }
    );
  }
})();
